import React, { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Box, CircularProgress, Alert } from '@mui/material';

// Configure PDF.js worker
// Use jsDelivr CDN URL for PDF.js worker (supports CORS, using .mjs module)
pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs';

export interface PageRenderInfo {
  pageNumber: number;
  pageWidth: number;    // PDF page width in points
  pageHeight: number;   // PDF page height in points
  renderWidth: number;  // Actual rendered width in pixels
  renderHeight: number; // Actual rendered height in pixels
  scale: number;        // Actual scale factor used for rendering
}

export interface PDFCanvasViewerProps {
  pdfUrl: string;
  scale?: number;
  onPageRenderSuccess?: (info: PageRenderInfo) => void;
  onDocumentLoadSuccess?: (numPages: number) => void;
  renderOverlay?: (pageNumber: number, pageInfo: PageRenderInfo) => React.ReactNode;
}

const PDFCanvasViewer: React.FC<PDFCanvasViewerProps> = ({
  pdfUrl,
  scale = 1.0,
  onPageRenderSuccess,
  onDocumentLoadSuccess,
  renderOverlay,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageRenderInfo, setPageRenderInfo] = useState<Map<number, PageRenderInfo>>(new Map());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const handleDocumentLoadSuccess = useCallback(
    ({ numPages: loadedPages }: { numPages: number }) => {
      console.log(`📄 PDF loaded successfully: ${loadedPages} pages`);
      setNumPages(loadedPages);
      setLoading(false);
      onDocumentLoadSuccess?.(loadedPages);
    },
    [onDocumentLoadSuccess]
  );

  const handleDocumentLoadError = useCallback((err: Error) => {
    console.error('❌ PDF load error:', err);
    setError(`Failed to load PDF: ${err.message}`);
    setLoading(false);
  }, []);

  const handlePageRenderSuccess = useCallback(
    (pageNumber: number) => {
      return (page: any) => {
        try {
          // Get the actual viewport used for rendering
          const viewport = page.getViewport({ scale });

          // Extract page dimensions
          const pageInfo: PageRenderInfo = {
            pageNumber,
            pageWidth: page.view[2],    // PDF page width in points (from PDF metadata)
            pageHeight: page.view[3],   // PDF page height in points (from PDF metadata)
            renderWidth: viewport.width,   // Rendered width in pixels
            renderHeight: viewport.height, // Rendered height in pixels
            scale: viewport.scale          // Actual scale factor
          };

          console.log(`✅ Page ${pageNumber} rendered:`, {
            pdfSize: `${pageInfo.pageWidth}x${pageInfo.pageHeight} points`,
            renderSize: `${pageInfo.renderWidth}x${pageInfo.renderHeight} px`,
            scale: pageInfo.scale
          });

          // Store page render info
          setPageRenderInfo((prev) => {
            const updated = new Map(prev);
            updated.set(pageNumber, pageInfo);
            return updated;
          });

          // Notify parent component
          onPageRenderSuccess?.(pageInfo);
        } catch (err) {
          console.error(`❌ Error processing page ${pageNumber} render:`, err);
        }
      };
    },
    [scale, onPageRenderSuccess]
  );

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Document
        file={pdfUrl}
        onLoadSuccess={handleDocumentLoadSuccess}
        onLoadError={handleDocumentLoadError}
        loading={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        {Array.from(new Array(numPages), (el, index) => {
          const pageNumber = index + 1;
          const pageInfo = pageRenderInfo.get(pageNumber);

          return (
            <Box
              key={`page_${pageNumber}`}
              sx={{
                position: 'relative',
                mb: 2, // Gap between pages
                bgcolor: 'white',
                boxShadow: 1,
              }}
            >
              {/* PDF Page Canvas */}
              <Page
                pageNumber={pageNumber}
                scale={scale}
                onRenderSuccess={handlePageRenderSuccess(pageNumber)}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />

              {/* Annotation Overlay (positioned absolutely over the page) */}
              {pageInfo && renderOverlay && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${pageInfo.renderWidth}px`,
                    height: `${pageInfo.renderHeight}px`,
                    pointerEvents: 'auto',
                    zIndex: 10,
                  }}
                >
                  {renderOverlay(pageNumber, pageInfo)}
                </Box>
              )}
            </Box>
          );
        })}
      </Document>
    </Box>
  );
};

export default PDFCanvasViewer;
